import React from 'react';

const sections = [
  {
    title: '1. Scope',
    body: [
      'This Privacy Policy explains how BillJaldi collects, uses, stores, shares, and protects information when users create invoices, save business data, manage customers/items, use paid plans, contact support, or access related BillJaldi services.',
      'By using BillJaldi, users confirm that they have authority to enter business, customer, client, invoice, tax, and contact information into the service.',
    ],
  },
  {
    title: '2. Information We Collect',
    body: [
      'Account and profile data may include name, email address, phone number, business name, GSTIN, state, address, logo, seal/signature image, login provider, plan, subscription status, and support requests.',
      'Business workflow data may include invoices, customer/client names, phone numbers, email addresses, GSTINs, addresses, products/items, HSN/SAC codes, prices, tax summaries, due dates, payment status, notes, exported files, and invoice PDFs or previews.',
      'Technical and security data may include device/browser information, IP-derived region, timestamps, pages visited, error logs, authentication events, usage counts, and actions needed to secure, debug, operate, and improve the service.',
      'Payment data may include plan, amount, currency, provider, subscription ID, payment ID, billing period, payment status, and related metadata. Full payment instrument details are handled by payment providers such as Razorpay according to their policies.',
    ],
  },
  {
    title: '3. How We Use Information',
    body: [
      'We use information to authenticate users, create invoices, generate PDFs, save history, autofill business details, manage customers and items, enforce plan limits, process subscriptions, provide support, detect abuse, improve reliability, and comply with legal obligations.',
      'Customer/client details entered by users may be processed for invoice sharing, reminders, due-date workflows, payment follow-ups, outstanding tracking, reporting, integrations, WhatsApp/email workflows, and other product features requested or enabled by the user.',
      'We may use operational, aggregated, or de-identified information for analytics, product improvement, pricing, fraud prevention, security, compliance, and legally permitted business purposes. We do not sell customer/client contact lists.',
    ],
  },
  {
    title: '4. Sharing And Processors',
    body: [
      'We may share information with service providers that help operate BillJaldi, including hosting, database, email/support, authentication, payments, analytics, security, and communication providers.',
      'If users enable or use integrations such as Razorpay, Meta/WhatsApp, Shopify, email, or other workflow tools, BillJaldi may transmit the relevant information necessary for that feature to work.',
      'We may disclose information if required by law, legal process, government request, fraud prevention, security investigation, enforcement of our terms, or protection of rights, users, or the public.',
    ],
  },
  {
    title: '5. User Responsibilities',
    body: [
      'Users are responsible for the accuracy, legality, and permissions associated with the data they enter. This includes making sure they have a lawful basis and required consent to store customer/client details and send invoice-related communications.',
      'Users must not upload unlawful, fraudulent, misleading, infringing, sensitive, or unnecessary personal information into BillJaldi.',
    ],
  },
  {
    title: '6. Retention And Deletion',
    body: [
      'We retain account, invoice, billing, support, security, and technical records for as long as needed to provide the service, meet legal/accounting obligations, resolve disputes, prevent abuse, maintain backups, and enforce agreements.',
      'Logged-out browser data may be temporary and may be cleared from the device. Logged-in account data is stored on BillJaldi systems unless deleted according to product controls, support processes, or legal retention requirements.',
    ],
  },
  {
    title: '7. Security',
    body: [
      'We use reasonable technical and organizational safeguards, including HTTPS in production, account-based access controls, server-side plan enforcement, and user-scoped database queries for account data. No internet service can guarantee absolute security.',
      'Users should protect their Google account, devices, browsers, and access to their workspace. Users should report suspicious access or data issues through BillJaldi support.',
    ],
  },
  {
    title: '8. Children',
    body: [
      'BillJaldi is intended for business use and is not directed to children. Users should not submit children’s personal information unless legally permitted and necessary for a lawful business purpose.',
    ],
  },
  {
    title: '9. Updates And Contact',
    body: [
      'We may update this Privacy Policy as the product, law, or operating practices change. Continued use of BillJaldi after updates means the updated policy applies.',
      'For privacy, account, billing, or legal requests, contact BillJaldi through the support options available inside the product or the official contact channel published on the website.',
    ],
  },
];

export default function Privacy() {
  return (
    <div className="max-w-[980px] mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-testid="privacy-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.18em] font-black text-orange-600 mb-2">Privacy</div>
        <h1 className="font-heading text-3xl sm:text-5xl font-black tracking-tight text-slate-900">Privacy Policy</h1>
        <p className="mt-3 text-sm leading-relaxed text-slate-500">
          This page explains what BillJaldi collects, why it is used, and how business, invoice, customer, and account data are handled.
        </p>
        <p className="mt-2 text-sm font-semibold leading-relaxed text-slate-600">Bill Jaldi is operated by Carpentry Works.</p>
        <p className="mt-2 text-xs font-semibold text-slate-400">Last updated: June 2026</p>
      </div>

      <div className="space-y-4">
        {sections.map((section) => (
          <section key={section.title} className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm">
            <h2 className="font-heading text-xl font-black text-slate-900">{section.title}</h2>
            <div className="mt-3 space-y-3">
              {section.body.map((paragraph) => (
                <p key={paragraph} className="text-sm leading-7 text-slate-600">{paragraph}</p>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
